import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import type { PublicShowcaseApplication } from '../shared/types'
import { relativeTime } from './format'
import { cardLine, heroPreview, hostOf, productPath } from './showcase-data'
import {
  HOUSTON_TIERSEL_DEMO,
  showcaseDemoFor,
} from './hackathon-demos'
import type { ShowcaseDemo } from './hackathon-demos'

export function useMedia(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const update = () => setMatches(list.matches)
    update()
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [query])
  return matches
}

type PreviewState = {
  demo: ShowcaseDemo
  visible: boolean
  reduced: boolean
}

const previewStates = new Map<HTMLVideoElement, PreviewState>()
const MAX_ACTIVE_PREVIEW_VIDEOS = 2
let filmOpen = false

function syncPreviewVideos() {
  const candidates = [...previewStates].filter(
    ([, state]) => state.visible && !state.reduced && !filmOpen && !document.hidden,
  )
  const wanted = new Set(candidates.slice(0, MAX_ACTIVE_PREVIEW_VIDEOS).map(([node]) => node))
  for (const [node, state] of previewStates) {
    if (!wanted.has(node)) {
      node.pause()
      continue
    }
    if (!node.src) {
      node.preload = 'metadata'
      node.src = state.demo.preview
      node.load()
    }
    void node.play().catch(() => {})
  }
}

export function ShowcaseVideo({
  demo,
  alt,
  className,
}: {
  demo: ShowcaseDemo
  alt: string
  className?: string
}) {
  const video = useRef<HTMLVideoElement>(null)
  const reduced = useMedia('(prefers-reduced-motion: reduce)')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = video.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setVisible((entry?.intersectionRatio ?? 0) >= 0.25),
      { rootMargin: '0px', threshold: [0, 0.25] },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const node = video.current
    if (!node) return

    const state: PreviewState = { demo, visible, reduced }
    previewStates.set(node, state)
    const onFilmOpen = () => {
      filmOpen = true
      syncPreviewVideos()
    }
    const onFilmClose = () => {
      filmOpen = false
      syncPreviewVideos()
    }
    state.visible = visible
    state.reduced = reduced
    syncPreviewVideos()
    document.addEventListener('visibilitychange', syncPreviewVideos)
    document.addEventListener('showcase-film-open', onFilmOpen)
    document.addEventListener('showcase-film-close', onFilmClose)
    return () => {
      document.removeEventListener('visibilitychange', syncPreviewVideos)
      document.removeEventListener('showcase-film-open', onFilmOpen)
      document.removeEventListener('showcase-film-close', onFilmClose)
      previewStates.delete(node)
      node.pause()
      syncPreviewVideos()
    }
  }, [demo.preview])

  useEffect(() => {
    const node = video.current
    const state = node ? previewStates.get(node) : undefined
    if (!state) return
    state.visible = visible
    state.reduced = reduced
    syncPreviewVideos()
  }, [reduced, visible])

  return (
    <video
      ref={video}
      className={className}
      poster={demo.poster}
      muted
      loop
      playsInline
      preload="none"
      aria-label={alt}
      data-preview-video="true"
    />
  )
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

function plainClick(event: MouseEvent) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}

/**
 * Two rows of live captures drifting in opposite directions. Scrolling pushes
 * them along, and scrolling up runs them backwards.
 *
 * Driven by the Web Animations API so the drift itself costs no JavaScript per
 * frame; only the playback rate follows the scroll velocity.
 */
export function Marquee({
  applications,
  onGo,
}: {
  applications: PublicShowcaseApplication[]
  onGo: (to: string) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const tracks = useRef<Array<HTMLDivElement | null>>([])
  const withShots = applications.filter((application) => heroPreview(application))
  const rows = [
    withShots.filter((_, index) => index % 2 === 0),
    withShots.filter((_, index) => index % 2 === 1),
  ]

  useEffect(() => {
    const node = box.current
    if (!node || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const animations = tracks.current.flatMap((track, index) => {
      if (!track || typeof track.animate !== 'function') return []
      const duration = Math.max(1, track.childElementCount / 2) * 5200
      const animation = track.animate(
        [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(-50%,0,0)' }],
        { duration, iterations: Infinity, direction: index % 2 ? 'reverse' : 'normal' },
      )
      // A head start, so a long scroll upwards can rewind without reaching the start.
      animation.currentTime = duration * 400
      return [animation]
    })

    let visible = false
    let frame = 0
    let lastY = window.scrollY
    let lastT = performance.now()
    let rate = 1
    const tick = (now: number) => {
      const dt = Math.max(1, now - lastT)
      const velocity = (window.scrollY - lastY) / dt
      lastY = window.scrollY
      lastT = now
      const target = clamp(1 + velocity * 2.2, -4, 6)
      rate += (target - rate) * 0.08
      if (Math.abs(rate - (animations[0]?.playbackRate ?? rate)) > 0.02) {
        animations.forEach((animation) => animation.updatePlaybackRate(rate))
      }
      if (visible) frame = requestAnimationFrame(tick)
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        animations.forEach((animation) => (visible ? animation.play() : animation.pause()))
        cancelAnimationFrame(frame)
        if (visible) {
          lastY = window.scrollY
          lastT = performance.now()
          frame = requestAnimationFrame(tick)
        }
      },
      { rootMargin: '120px 0px' },
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
      animations.forEach((animation) => animation.cancel())
    }
  }, [withShots.length])

  if (withShots.length < 4) return null
  return (
    <section className="sc-marquee" aria-label="Every product, captured live" ref={box}>
      {rows.map((row, rowIndex) => (
        <div className="sc-marquee-row" key={rowIndex}>
          <div
            className="sc-marquee-track"
            ref={(node) => {
              tracks.current[rowIndex] = node
            }}
          >
            {[0, 1].flatMap((copy) =>
              row.map((application) => {
                const preview = heroPreview(application)!
                const to = productPath(application.id)
                return (
                  <a
                    key={`${copy}-${application.id}`}
                    className="sc-tile"
                    href={to}
                    aria-hidden={copy === 1 || undefined}
                    tabIndex={copy === 1 ? -1 : undefined}
                    style={{ '--accent': application.accent ?? '#c8a664' } as CSSProperties}
                    onClick={(event) => {
                      if (!plainClick(event)) return
                      event.preventDefault()
                      onGo(to)
                    }}
                  >
                    <img
                      src={preview.screenshot.imageUrl}
                      alt={copy === 1 ? '' : application.name}
                      loading="lazy"
                      decoding="async"
                      width={preview.screenshot.width}
                      height={preview.screenshot.height}
                    />
                    <span className="sc-tile-label">
                      <i />
                      {application.name}
                    </span>
                  </a>
                )
              }),
            )}
          </div>
        </div>
      ))}
    </section>
  )
}

function ReelCard({
  application,
  index,
  total,
  onGo,
  status,
}: {
  application: PublicShowcaseApplication
  index: number
  total: number
  onGo: (to: string) => void
  status: ReactNode
}) {
  const preview = heroPreview(application)
  const demo = showcaseDemoFor(application)
  const to = productPath(application.id)
  const activity = application.activity
  return (
    <article
      className="sc-reel-card"
      style={{ '--accent': application.accent ?? '#c8a664' } as CSSProperties}
    >
      <a
        className="sc-reel-card-main"
        href={to}
        onClick={(event) => {
          if (!plainClick(event)) return
          event.preventDefault()
          const image = event.currentTarget.querySelector('img')
          if (image) image.style.viewTransitionName = 'sc-hero-shot'
          onGo(to)
        }}
      >
        <div className="sc-reel-shot">
          {demo ? (
            <ShowcaseVideo
              demo={demo}
              alt={`${application.name} demo preview`}
              className="sc-reel-media"
            />
          ) : preview ? (
            <img
              className="sc-reel-media"
              src={preview.screenshot.imageUrl}
              alt={`${application.name} — ${preview.screenshot.pageTitle ?? preview.name}`}
              width={preview.screenshot.width}
              height={preview.screenshot.height}
              loading={index < 2 ? 'eager' : 'lazy'}
              decoding="async"
            />
          ) : application.iconUrl ? (
            <img
              className="sc-reel-media sc-card-icon"
              src={application.iconUrl}
              alt=""
              loading="lazy"
              decoding="async"
              style={{
                objectFit: application.iconFit ?? 'contain',
                objectPosition: application.iconPosition ?? 'center',
              }}
            />
          ) : (
            <span className="sc-card-monogram" aria-hidden="true">
              {application.name.slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className="sc-reel-host">{hostOf(application)}</span>
        </div>
        <div className="sc-reel-body">
          <div className="sc-reel-top">
            <span className="sc-reel-index" aria-hidden="true">
              {String(index + 1).padStart(2, '0')}
              <span>/{String(total).padStart(2, '0')}</span>
            </span>
            <h3 className="sc-reel-title">{application.name}</h3>
            {status}
          </div>
          <p className="sc-reel-line">{demo?.tagline ?? cardLine(application.description)}</p>
          {activity && activity.commits30d > 0 && (
            <p className="sc-reel-meta">
              <span>
                <b>{activity.commits7d ?? 0}</b> commits this week
              </span>
              <span>
                <b>{activity.commits30d}</b> in 30 days
              </span>
              {activity.lastCommitAt && <span>last {relativeTime(activity.lastCommitAt)}</span>}
            </p>
          )}
        </div>
      </a>
      <nav className="sc-reel-links" aria-label={`${application.name} links`}>
        {demo ? (
          <>
            <a href={demo.demo} target="_blank" rel="noreferrer">
              Demo ↗
            </a>
            {demo.pitch && (
              <a href={demo.pitch} target="_blank" rel="noreferrer">
                Deck ↗
              </a>
            )}
            <a href={demo.app} target="_blank" rel="noreferrer">
              Live ↗
            </a>
            {demo.devpost && (
              <a href={demo.devpost} target="_blank" rel="noreferrer">
                Devpost ↗
              </a>
            )}
            {application.id === 'jevvy' && (
              <a
                href={HOUSTON_TIERSEL_DEMO.devpost}
                target="_blank"
                rel="noreferrer"
              >
                Houston Devpost ↗
              </a>
            )}
          </>
        ) : (
          <a href={application.homepage} target="_blank" rel="noreferrer">
            Live ↗
          </a>
        )}
      </nav>
    </article>
  )
}

/**
 * The lead products, as a reel.
 *
 * Where there is room, the section pins and vertical scroll drives the reel
 * sideways: the centred card is full size and flat, its neighbours turn away
 * and dim. Everywhere else — touch, narrow, short, or reduced motion — it is a
 * native snap carousel, because hijacking a thumb's scroll is worse than any
 * effect it buys.
 */
export function FeaturedReel({
  applications,
  onGo,
  statusFor,
  header,
}: {
  applications: PublicShowcaseApplication[]
  onGo: (to: string) => void
  statusFor: (application: PublicShowcaseApplication) => ReactNode
  header: ReactNode
}) {
  const roomy = useMedia('(min-width: 1024px) and (min-height: 680px) and (hover: hover)')
  const reduced = useMedia('(prefers-reduced-motion: reduce)')
  const pinned = roomy && !reduced
  const section = useRef<HTMLElement>(null)
  const track = useRef<HTMLDivElement>(null)
  const bar = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const total = applications.length

  useEffect(() => {
    const host = section.current
    const rail = track.current
    if (!pinned || !host || !rail) return
    const cards = Array.from(rail.children) as HTMLElement[]
    let step = 1
    let current = 0
    let frame = 0
    let running = false
    let lastActive = -1

    const measure = () => {
      const first = cards[0]
      const second = cards[1]
      step = first && second ? second.offsetLeft - first.offsetLeft : first?.offsetWidth ?? 1
    }
    const target = () => {
      const top = host.getBoundingClientRect().top
      const span = host.offsetHeight - window.innerHeight
      const progress = span > 0 ? clamp(-top / span, 0, 1) : 0
      return { x: progress * step * (cards.length - 1), progress }
    }
    const paint = () => {
      const goal = target()
      current += (goal.x - current) * 0.12
      if (Math.abs(goal.x - current) < 0.3) current = goal.x
      rail.style.transform = `translate3d(${-current}px,0,0)`
      cards.forEach((card, index) => {
        const distance = (index * step - current) / step
        const reach = Math.min(Math.abs(distance), 1.6)
        card.style.transform = `perspective(1800px) rotateY(${clamp(-distance * 16, -22, 22)}deg) scale(${1 - reach * 0.1})`
        card.style.opacity = String(1 - reach * 0.42)
      })
      if (bar.current) bar.current.style.transform = `scaleX(${goal.progress})`
      const next = clamp(Math.round(current / step), 0, cards.length - 1)
      if (next !== lastActive) {
        lastActive = next
        setActive(next)
      }
      if (current !== goal.x) frame = requestAnimationFrame(paint)
      else running = false
    }
    const wake = () => {
      if (running) return
      running = true
      frame = requestAnimationFrame(paint)
    }
    const onResize = () => {
      measure()
      current = target().x
      wake()
    }
    measure()
    current = target().x
    wake()
    const resized = new ResizeObserver(onResize)
    resized.observe(rail)
    window.addEventListener('scroll', wake, { passive: true })
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(frame)
      resized.disconnect()
      window.removeEventListener('scroll', wake)
      window.removeEventListener('resize', onResize)
      rail.style.transform = ''
      cards.forEach((card) => {
        card.style.transform = ''
        card.style.opacity = ''
      })
    }
  }, [pinned, total])

  useEffect(() => {
    const rail = track.current
    if (pinned || !rail) return
    let frame = 0
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const first = rail.children[0] as HTMLElement | undefined
        const second = rail.children[1] as HTMLElement | undefined
        if (!first) return
        const step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth
        setActive(clamp(Math.round(rail.scrollLeft / step), 0, total - 1))
      })
    }
    rail.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      rail.removeEventListener('scroll', onScroll)
    }
  }, [pinned, total])

  const goTo = (index: number) => {
    const rail = track.current
    const host = section.current
    if (!rail || !host) return
    if (pinned) {
      const span = host.offsetHeight - window.innerHeight
      const top = host.getBoundingClientRect().top + window.scrollY
      window.scrollTo({
        top: top + (span * index) / Math.max(1, total - 1),
        behavior: reduced ? 'auto' : 'smooth',
      })
    } else {
      const card = rail.children[index] as HTMLElement | undefined
      const first = rail.children[0] as HTMLElement | undefined
      if (card && first) {
        rail.scrollTo({
          left: card.offsetLeft - first.offsetLeft,
          behavior: reduced ? 'auto' : 'smooth',
        })
      }
    }
  }

  const dots = (
    <div className="sc-reel-dots">
      {applications.map((application, index) => (
        <button
          key={application.id}
          type="button"
          aria-label={`Show ${application.name}`}
          aria-current={index === active}
          onClick={() => goTo(index)}
        />
      ))}
    </div>
  )

  const cards = applications.map((application, index) => (
    <ReelCard
      key={application.id}
      application={application}
      index={index}
      total={total}
      onGo={onGo}
      status={statusFor(application)}
    />
  ))

  if (!pinned) {
    return (
      <section className="sc-reel sc-reel-snap" id="work" ref={section}>
        <div className="sc-container">{header}</div>
        <div className="sc-reel-rail" ref={track}>
          {cards}
        </div>
        <div className="sc-container sc-reel-foot">
          {dots}
          <div className="sc-reel-arrows">
            <button
              type="button"
              aria-label="Previous"
              disabled={active === 0}
              onClick={() => goTo(active - 1)}
            >
              ←
            </button>
            <button
              type="button"
              aria-label="Next"
              disabled={active === total - 1}
              onClick={() => goTo(active + 1)}
            >
              →
            </button>
          </div>
        </div>
      </section>
    )
  }

  const accent = applications[active]?.accent ?? '#c8a664'
  return (
    <section
      className="sc-reel sc-reel-pinned"
      id="work"
      ref={section}
      style={{ height: `${total * 70 + 60}vh` }}
    >
      <div className="sc-reel-stage" style={{ '--accent': accent } as CSSProperties}>
        <div className="sc-reel-glow" aria-hidden="true" />
        <div className="sc-container sc-reel-head">
          {header}
          <span className="sc-reel-counter" aria-hidden="true">
            {String(active + 1).padStart(2, '0')}
            <span>/{String(total).padStart(2, '0')}</span>
          </span>
        </div>
        <div className="sc-reel-viewport">
          <div className="sc-reel-rail" ref={track}>
            {cards}
          </div>
        </div>
        <div className="sc-container sc-reel-foot">
          <div className="sc-reel-progress" aria-hidden="true">
            <div ref={bar} />
          </div>
          {dots}
        </div>
      </div>
    </section>
  )
}
